import { LitElement, html, css, svg, nothing } from "lit";
import { customElement, property, state, query } from "lit/decorators.js";

/** One plotted point: a UTC day (YYYY-MM-DD) and that day's value. */
export interface UsagePoint {
  date: string;
  value: number;
}

// Chart geometry, in viewBox units (the SVG scales responsively to its box).
const W = 680;
const H = 200;
const PAD_L = 52;
const PAD_R = 14;
const PAD_T = 14;
const PAD_B = 26;
const INNER_W = W - PAD_L - PAD_R;
const INNER_H = H - PAD_T - PAD_B;
const BASE_Y = PAD_T + INNER_H;

/** SVG path for a bar with rounded top corners, anchored to the baseline. */
function barPath(x: number, y: number, w: number, h: number, r: number): string {
  const rad = Math.min(r, w / 2, h);
  return (
    `M${x},${y + h} L${x},${y + rad} Q${x},${y} ${x + rad},${y} ` +
    `L${x + w - rad},${y} Q${x + w},${y} ${x + w},${y + rad} L${x + w},${y + h} Z`
  );
}

function shortDate(iso: string): string {
  return new Date(`${iso}T00:00:00Z`).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    timeZone: "UTC",
  });
}

/**
 * usage-chart is a minimal, token-styled bar chart for a single metered
 * dimension over a daily window. It plots each day as a celeste bar anchored to
 * the baseline, draws a recessive 0/mid/max gridline pair with axis labels, and
 * carries a hover tooltip (date + formatted value). Identity comes from the
 * enclosing card title and the x-axis — never color alone — and a keyboard-
 * reachable <details> data table gives the full accessible alternative.
 *
 *   <usage-chart .label=${"Builds"} .points=${pts} .format=${fmt}></usage-chart>
 */
@customElement("usage-chart")
export class UsageChart extends LitElement {
  /** Daily points across the full window (zeros included for quiet days). */
  @property({ attribute: false }) points: UsagePoint[] = [];
  /** Dimension label, used in the accessible summary. */
  @property() label = "";
  /** Long formatter with unit, for tooltip + summary (e.g. "12 builds"). */
  @property({ attribute: false }) format: (n: number) => string = (n) => String(n);
  /** Compact formatter for the y-axis ticks (short unit so it fits); defaults to `format`. */
  @property({ attribute: false }) axisFormat?: (n: number) => string;

  @state() private hover?: { i: number; px: number; py: number };

  @query("svg") private svgEl?: SVGSVGElement;

  static override styles = css`
    :host {
      display: block;
      position: relative;
    }
    svg {
      display: block;
      width: 100%;
      height: auto;
      touch-action: none;
    }
    .grid {
      stroke: var(--bino-border);
      stroke-width: 1;
    }
    .axis-text {
      fill: var(--bino-text-secondary);
      font-family: var(--bino-font-mono);
      font-size: var(--bino-font-size-sm);
    }
    .bar {
      fill: var(--bino-primary);
      transition: fill var(--bino-transition-fast);
    }
    .bar.hot {
      fill: var(--bino-primary-hover);
    }
    .tip {
      position: absolute;
      z-index: 1;
      transform: translate(-50%, calc(-100% - var(--bino-space-xs)));
      pointer-events: none;
      white-space: nowrap;
      background: var(--bino-surface);
      border: 1px solid var(--bino-border);
      border-radius: var(--bino-radius-sm);
      box-shadow: var(--bino-shadow-dropdown);
      padding: var(--bino-space-xs) var(--bino-space-sm);
      font-family: var(--bino-font-sans);
      font-size: var(--bino-font-size-sm);
    }
    .tip .d {
      color: var(--bino-text-secondary);
    }
    .tip .v {
      color: var(--bino-text);
      font-weight: 600;
      font-family: var(--bino-font-mono);
    }
    .empty {
      padding: var(--bino-space-xl) var(--bino-space-md);
      text-align: center;
      color: var(--bino-text-secondary);
      font-family: var(--bino-font-sans);
      font-size: var(--bino-font-size-base);
    }
    details {
      margin-top: var(--bino-space-md);
    }
    summary {
      cursor: pointer;
      font-family: var(--bino-font-sans);
      font-size: var(--bino-font-size-sm);
      color: var(--bino-text-secondary);
      width: fit-content;
    }
    summary:focus-visible {
      outline: none;
      box-shadow: 0 0 0 3px var(--bino-focus-ring);
      border-radius: var(--bino-radius-sm);
    }
    table {
      width: 100%;
      border-collapse: collapse;
      margin-top: var(--bino-space-sm);
      font-size: var(--bino-font-size-sm);
    }
    th,
    td {
      text-align: left;
      padding: var(--bino-space-xs) var(--bino-space-sm);
      border-bottom: 1px solid var(--bino-border);
    }
    th {
      color: var(--bino-text-secondary);
      font-weight: 600;
      font-family: var(--bino-font-sans);
    }
    td {
      font-family: var(--bino-font-mono);
      color: var(--bino-text);
    }
    td.date {
      color: var(--bino-text-secondary);
    }
  `;

  private slotW(): number {
    return INNER_W / Math.max(1, this.points.length);
  }

  private onMove(e: PointerEvent) {
    const el = this.svgEl;
    const n = this.points.length;
    if (!el || n === 0) return;
    const rect = el.getBoundingClientRect();
    const scale = rect.width / W;
    const xInSvg = (e.clientX - rect.left) / scale;
    const i = Math.floor((xInSvg - PAD_L) / this.slotW());
    if (i < 0 || i >= n) {
      this.hover = undefined;
      return;
    }
    this.hover = { i, px: e.clientX - rect.left, py: e.clientY - rect.top };
  }

  private onLeave() {
    this.hover = undefined;
  }

  override render() {
    const pts = this.points;
    const n = pts.length;
    const values = pts.map((p) => p.value);
    const maxVal = Math.max(0, ...values);

    if (n === 0 || maxVal <= 0) {
      return html`<div class="empty" role="img" aria-label=${`${this.label}: no activity in this period`}>
        No activity in this period
      </div>`;
    }

    const slot = this.slotW();
    const barW = Math.max(1, Math.min(slot - 2, 40));
    const y = (v: number) => PAD_T + INNER_H * (1 - v / maxVal);

    const gridLines = [0, 0.5, 1].map((f) => {
      const gy = PAD_T + INNER_H * (1 - f);
      return svg`<line class="grid" x1=${PAD_L} y1=${gy} x2=${W - PAD_R} y2=${gy} />`;
    });
    const axisFmt = this.axisFormat ?? this.format;
    const axisLabels = [0, 1].map((f) => {
      const gy = PAD_T + INNER_H * (1 - f);
      return svg`<text class="axis-text" x=${PAD_L - 6} y=${gy} text-anchor="end" dominant-baseline="central">${axisFmt(f * maxVal)}</text>`;
    });

    const tickEvery = Math.max(1, Math.ceil(n / 6));
    const ticks = pts.map((p, i) => {
      if (i % tickEvery !== 0 && i !== n - 1) return nothing;
      const tx = PAD_L + i * slot + slot / 2;
      return svg`<text class="axis-text" x=${tx} y=${BASE_Y + 16} text-anchor="middle">${shortDate(p.date)}</text>`;
    });

    const bars = pts.map((p, i) => {
      if (p.value <= 0) return nothing;
      const bx = PAD_L + i * slot + (slot - barW) / 2;
      const by = y(p.value);
      const h = Math.max(1, BASE_Y - by);
      return svg`<path class=${`bar ${this.hover?.i === i ? "hot" : ""}`} d=${barPath(bx, by, barW, h, 3)} />`;
    });

    const total = values.reduce((a, b) => a + b, 0);
    const peakIdx = values.indexOf(maxVal);
    const summary = `${this.label}: ${this.format(total)} total over ${n} days, peak ${this.format(maxVal)} on ${shortDate(pts[peakIdx].date)}.`;

    const h = this.hover;
    const active = h ? pts[h.i] : undefined;

    return html`
      <svg
        viewBox="0 0 ${W} ${H}"
        preserveAspectRatio="xMidYMid meet"
        role="img"
        aria-label=${summary}
        @pointermove=${this.onMove}
        @pointerleave=${this.onLeave}
      >
        ${gridLines} ${axisLabels} ${ticks} ${bars}
      </svg>
      ${h && active
        ? html`<div class="tip" style=${`left:${h.px}px;top:${h.py}px`}>
            <span class="d">${shortDate(active.date)}</span> &nbsp;<span class="v"
              >${this.format(active.value)}</span
            >
          </div>`
        : nothing}
      <details>
        <summary>Data table</summary>
        <table>
          <thead>
            <tr>
              <th scope="col">Date</th>
              <th scope="col">${this.label}</th>
            </tr>
          </thead>
          <tbody>
            ${pts
              .filter((p) => p.value > 0)
              .map(
                (p) => html`<tr>
                  <td class="date">${shortDate(p.date)}</td>
                  <td>${this.format(p.value)}</td>
                </tr>`,
              )}
          </tbody>
        </table>
      </details>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "usage-chart": UsageChart;
  }
}
